import { useMutation } from '@tanstack/react-query';

import axios from 'axios';
import { MutationConfig, queryClient } from 'lib/react-query';

import { SnapshotAlarmSetting, CreateSnapshotAlarmSettingDto } from 'types';
import { convertAlarmSettings } from 'api/dtoConverters';

export const updateAlarmSettings = async ({
  data,
  alarmSettingId,
}: {
  data: CreateSnapshotAlarmSettingDto;
  alarmSettingId: number;
}): Promise<SnapshotAlarmSetting> => {
  const { data: updatedSettings } = await axios.put(
    `/api/snapshots/alarms/snapshot-alarm-settings/${alarmSettingId}`,
    convertAlarmSettings(data)
  );
  return updatedSettings as SnapshotAlarmSetting;
};

type UseUpdateAlarmSettingsOptions = {
  config?: MutationConfig<typeof updateAlarmSettings>;
};

export const useUpdateAlarmSettings = ({
  config,
}: UseUpdateAlarmSettingsOptions = {}) => {
  return useMutation({
    onMutate: async (updatingAlarmSettings) => {
      await queryClient.cancelQueries([
        'alarms',
        'settings',
        updatingAlarmSettings?.alarmSettingId,
      ]);

      const previousSnapshotAlarmSettings =
        queryClient.getQueryData<SnapshotAlarmSetting>([
          'alarms',
          'settings',
          updatingAlarmSettings?.alarmSettingId,
        ]);

      queryClient.setQueryData(
        ['alarms', 'settings', updatingAlarmSettings?.alarmSettingId],
        {
          ...previousSnapshotAlarmSettings,
          ...updatingAlarmSettings.data,
          id: updatingAlarmSettings.alarmSettingId,
        }
      );

      return previousSnapshotAlarmSettings;
    },
    onError: (_, __, context: any) => {
      if (context?.previousSnapshotAlarmSettings) {
        queryClient.setQueryData(
          ['switches', context.previousSnapshotAlarmSettings.id],
          context.previousSwitches
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['alarms', 'settings']);
    },
    ...config,
    mutationFn: updateAlarmSettings,
  });
};
